import moment from 'moment';

/**
 * Declaración de los filtros de la grilla de Facturas para <GridFilterPanel />.
 *
 * Vive en su propio módulo porque lo consumen DOS lados: el panel (para renderizar)
 * y FacturasGrid (para calcular el query por defecto con las mismas reglas de
 * serialización). Antes esa lista estaba duplicada en los dos archivos.
 */

export const FACTURAS_FILTROS_STORAGE_KEY = 'FacturasGrid_filtros';

/**
 * Valores por defecto: rango de fecha de creación = último mes, para que la primera
 * petición ya vaya filtrada (objetivo de performance de VEC-3413).
 * Es una función, no una constante, porque "hoy" cambia entre sesiones.
 */
export const getFacturasFilterDefaults = () => ({
	fechaCreacionDesde: moment().subtract(1, 'month').format('YYYY-MM-DD'),
	fechaCreacionHasta: moment().format('YYYY-MM-DD'),
});

/**
 * @param flags.mostrarNroPrefacturacion  facturas.prefacturacion.habilitado
 * @param flags.mostrarMetodoDePago       tickets.metodoPago.habilitado + permiso METODOS_PAGO_LISTAR
 * @param flags.onBuscarTickets           (texto) => Promise<[{value,label}]>: busca los tickets del
 *                                        selector "Ticket" en el servidor mientras se tipea
 */
export const buildFacturasFilterFields = (flags) => {
	const opciones = flags || {};

	return [
		{
			name: 'fechaCreacionDesde',
			type: 'date',
			messageId: 'facturasGrid.filtros.fecha_creacion_desde',
			label: 'Fecha creación desde',
		},
		{
			name: 'fechaCreacionHasta',
			type: 'date',
			messageId: 'facturasGrid.filtros.fecha_creacion_hasta',
			label: 'Fecha creación hasta',
		},
		{
			name: 'nroPrefacturacion',
			type: 'text',
			messageId: 'facturasGrid.filtros.nro_prefacturacion',
			label: 'Nro. Prefacturación',
			visible: opciones.mostrarNroPrefacturacion === true,
		},
		{
			name: 'gerenciador',
			type: 'multiselect',
			messageId: 'facturasGrid.filtros.gerenciador',
			label: 'Gerenciador',
		},
		{
			name: 'tipo',
			type: 'text',
			messageId: 'facturasGrid.filtros.tipo',
			label: 'Tipo',
		},
		{
			name: 'puntoDeVenta',
			type: 'text',
			messageId: 'facturasGrid.filtros.punto_de_venta',
			label: 'Punto de Venta',
		},
		{
			name: 'numero',
			type: 'text',
			messageId: 'facturasGrid.filtros.numero',
			label: 'Número',
		},
		{
			// El backend parsea este filtro como d/m/Y (FacturasRepository::grid,
			// case 'fecha'), distinto del rango de fecha de creación.
			name: 'fecha',
			type: 'date',
			dateOutputFormat: 'DD/MM/YYYY',
			messageId: 'facturasGrid.filtros.fecha',
			label: 'Fecha',
		},
		{
			name: 'metodoDePago',
			type: 'multiselect',
			messageId: 'facturasGrid.filtros.metodo_de_pago',
			label: 'Método de pago',
			visible: opciones.mostrarMetodoDePago === true,
		},
		{
			name: 'quincena',
			type: 'multiselect',
			messageId: 'facturasGrid.filtros.quincena',
			label: 'Quincena',
		},
		{
			name: 'mes',
			type: 'multiselect',
			messageId: 'facturasGrid.filtros.mes',
			label: 'Mes',
		},
		{
			name: 'anio',
			type: 'text',
			messageId: 'facturasGrid.filtros.anio',
			label: 'Año',
		},
		{
			name: 'importe',
			type: 'text',
			messageId: 'facturasGrid.filtros.importe',
			label: 'Importe',
		},
		{
			name: 'estado',
			type: 'multiselect',
			messageId: 'facturasGrid.filtros.estado',
			label: 'Estado',
		},
		{
			// VEC-4067: tickets asociados a las facturas (directos y vía OT). Es un `pastableMultiselect` y no
			// un `multiselect` precargado porque la lista crece con los datos del cliente (decenas de miles de
			// tickets facturados): las opciones se buscan en el servidor mientras se tipea
			// (/facturas/tickets-select?busqueda=). Manda DOS claves: `tickets` con los chips elegidos y
			// `ticketsTexto` con lo que quedó escrito sin elegir; armarBusquedaTickets las junta en el valor
			// que espera el backend. No declara `onResolve`: un texto pegado ("1658, 1659") se busca igual.
			name: 'tickets',
			textName: 'ticketsTexto',
			type: 'pastableMultiselect',
			messageId: 'facturasGrid.filtros.tickets',
			label: 'Ticket',
			onSearch: opciones.onBuscarTickets,
			// Un ticket es un número: el campo solo admite dígitos y separadores (QA de VEC-4067).
			sanitizarTexto: soloNumerosYSeparadores,
		},
	];
};

/**
 * VEC-4067 (QA): lo que admite el campo de texto del filtro "Ticket": solo dígitos y los separadores de una
 * lista pegada (coma, punto y coma, espacio). Cada número se corta en 10 dígitos (un id de ticket nunca llega
 * a tanto), así un número larguísimo no termina en una búsqueda que no encuentra nada.
 */
export const soloNumerosYSeparadores = (texto) =>
	String(texto || '').replace(/[^\d,;\s]/g, '').replace(/\d{11,}/g, (numero) => numero.slice(0, 10));

/**
 * VEC-4067: valor de la columna `tickets` de la grilla a partir de las dos claves del filtro.
 *
 * El backend recibe valores separados por coma: los numéricos son los tickets elegidos como chips
 * ("1658,1659") y los que empiezan con "~" son texto que quedó escrito sin elegir ("~165"), una
 * búsqueda parcial por número. Un texto pegado con varios números ("1658, 1659") se separa en uno por
 * número. De cada uno se toman solo los dígitos ("12ab" busca 12) y lo que no tiene ninguno se ignora (un ticket
 * es un número).
 */
export const armarBusquedaTickets = (chips, texto) => {
	const ids = String(chips || '').split(',').map((v) => v.trim()).filter((v) => /^\d+$/.test(v));
	const parciales = String(texto || '').split(/[,;\s]+/).map((v) => v.replace(/\D/g, '').slice(0, 10)).filter((v) => v !== '').map((v) => '~' + v);
	return ids.concat(parciales).join(',');
};
